import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Col,
  Form,
  FormInstance,
  Input,
  Row,
  Select,
  Spin,
} from "antd";
import "./index.css";
import { WifiConnectArgs } from "../../types";
import { Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { BLUETOOTH_STATES } from "../../providers/bluetoothProvider";

const { Text } = Typography;

type Props = {
  form: FormInstance<WifiConnectArgs>;
  bluetoothState: string;
  wifiList: string[];
  currentNetwork: string | null;
  isWifiConnected: boolean;
  handleFinish: (values: WifiConnectArgs) => Promise<void>;
  handleRescan: () => void;
};

const DeviceSetupForm: React.FC<Props> = ({
  form,
  bluetoothState,
  wifiList,
  currentNetwork,
  isWifiConnected,
  handleRescan,
  handleFinish,
}) => {
  const [ssidSearch, setSsidSearch] = useState("");
  const [selectedSSID, setSelectedSSID] = useState<string | undefined>();
  const [hasSelectedCurrent, setHasSelectedCurrent] = useState(false);

  const onSearch = (value: string) => {
    if (value.length > 0) {
      setSsidSearch(value);
    }
  };

  const onBlurSelect = () => {
    if ((ssidSearch?.length || 0) > 0) {
      form.setFieldValue("ssid", ssidSearch);
      setHasSelectedCurrent(ssidSearch === currentNetwork);
    }
  };

  const onClearSelect = () => {
    setSsidSearch("");
  };

  const onSsidSelect = () => {
    setSsidSearch("");
  };

  const handleSelectValueChange = useCallback(
    (value: string) => {
      setHasSelectedCurrent(value === currentNetwork);
      setSelectedSSID(value);
      form.setFieldValue("ssid", value);
      form.setFieldValue("psk", "");
    },
    [currentNetwork, form],
  );

  // change selectedSSID when currentNetwork changes
  useEffect(() => {
    if (currentNetwork) {
      handleSelectValueChange(currentNetwork);
    }
  }, [currentNetwork, handleSelectValueChange]);

  // update hasSelectedCurrent when isWifiConnected changes
  useEffect(() => {
    if (currentNetwork) {
      setHasSelectedCurrent(
        isWifiConnected && form.getFieldValue("ssid") === currentNetwork,
      );
    }
  }, [isWifiConnected, currentNetwork, form]);

  const ssidMenuItems = [
    ...wifiList,
    ...(!wifiList.includes(ssidSearch) ? [ssidSearch] : []),
  ].map((ssid: string) => ({ value: ssid, label: <span>{ssid}</span> }));

  const wifiForm = (
    <Form
      name={"wifi-form"}
      form={form}
      className="device-setup-form"
      onFinish={handleFinish}
      initialValues={{ ssid: currentNetwork }}
    >
      <Form.Item name="Ssid">
        <Row gutter={[8, 0]}>
          <Col span={24} className="mb-1">
            <Text>Network connection</Text>
          </Col>
          <Col span={18}>
            <Form.Item name="ssid" noStyle>
              <Select
                id="ssid-select"
                placeholder={"Select network"}
                disabled={bluetoothState !== BLUETOOTH_STATES.CONNECTED}
                loading={bluetoothState === BLUETOOTH_STATES.SCANNING}
                allowClear
                showSearch
                onSearch={onSearch}
                onBlur={onBlurSelect}
                onClear={onClearSelect}
                onSelect={onSsidSelect}
                options={ssidMenuItems}
                value={selectedSSID}
                onChange={handleSelectValueChange}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            {bluetoothState === BLUETOOTH_STATES.CONNECTED ? (
              <Button
                type="default"
                icon={<ReloadOutlined />}
                className="device-setup-icon-button"
                onClick={handleRescan}
              >
                Refresh
              </Button>
            ) : (
              <Spin />
            )}
          </Col>
        </Row>
      </Form.Item>
      <Form.Item name="psk">
        <Row gutter={[8, 0]}>
          <Col span={24} className="mb-1">
            <Text>Password</Text>
          </Col>
          <Col span={24}>
            <Form.Item name="psk" noStyle>
              <Input.Password
                placeholder={"Enter password"}
                disabled={hasSelectedCurrent}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>
    </Form>
  );

  // main
  if (bluetoothState === BLUETOOTH_STATES.CONNECTING) {
    return <Spin />;
  }

  if (
    [
      BLUETOOTH_STATES.CONNECTED,
      BLUETOOTH_STATES.SCANNING,
      BLUETOOTH_STATES.WIFI_CONNECTING,
    ].includes(bluetoothState)
  ) {
    if (ssidMenuItems.length > 0) {
      return wifiForm;
    } else {
      return <Spin />;
    }
  }
  return null;
};

export default DeviceSetupForm;
